import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Plus, LogOut } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";

const EMPTY = { slug: "", name: "", tagline_de: "", tagline_en: "", body_de: "", body_en: "", image: "", featured: false, status: "upcoming", order: 10 };

export default function AdminDashboard() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("gateways");
  const [gateways, setGateways] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/gateways").then((r) => setGateways(r.data)).catch(() => {});
    api.get("/contact").then((r) => setContacts(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addGateway = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/gateways", { ...form, order: Number(form.order) || 10 });
      setForm(EMPTY);
      load();
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/gateways/${id}`);
    load();
  };

  const doLogout = async () => {
    await logout();
    nav("/admin/login");
  };

  const field = "w-full bg-[#0b3038] border border-[#F7F5F0]/15 px-4 py-2.5 font-body text-[#F7F5F0] text-sm placeholder-[#F7F5F0]/40 focus:outline-none focus:border-[#C5A880] transition-colors";

  return (
    <div className="min-h-screen bg-[#062029] text-[#F7F5F0]" data-testid="admin-dashboard">
      <header className="border-b border-[#F7F5F0]/12 px-6 md:px-10 h-20 flex items-center justify-between sticky top-0 bg-[#062029]/95 backdrop-blur z-10">
        <span className="font-serif-display text-2xl">SBG Admin</span>
        <div className="flex items-center gap-6">
          <button onClick={() => setTab("gateways")} className={`label ${tab === "gateways" ? "text-[#C5A880]" : "text-[#F7F5F0]/60"}`} data-testid="tab-gateways">Gateways</button>
          <button onClick={() => setTab("contacts")} className={`label ${tab === "contacts" ? "text-[#C5A880]" : "text-[#F7F5F0]/60"}`} data-testid="tab-contacts">Inquiries</button>
          <button onClick={doLogout} className="flex items-center gap-2 label text-[#F7F5F0]/60 hover:text-[#C5A880]" data-testid="admin-logout"><LogOut size={14} /> Logout</button>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-12">
        {tab === "gateways" ? (
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="font-serif-display text-3xl mb-8">Gateways</h2>
              <div className="space-y-4">
                {gateways.map((g) => (
                  <div key={g.id} className="flex items-center justify-between border border-[#F7F5F0]/12 px-5 py-4" data-testid={`admin-gateway-${g.slug}`}>
                    <div>
                      <p className="font-serif-display text-xl">{g.name} {g.featured && <span className="text-[#C5A880] text-xs">★</span>}</p>
                      <p className="text-[#F7F5F0]/50 text-xs uppercase tracking-wide">{g.status}</p>
                    </div>
                    <button onClick={() => del(g.id)} className="text-[#F7F5F0]/40 hover:text-[#e0a0a0]" data-testid={`delete-${g.slug}`}><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="font-serif-display text-3xl mb-8">Add Gateway</h2>
              <form onSubmit={addGateway} className="space-y-4" data-testid="add-gateway-form">
                <input className={field} placeholder="Slug (e.g. italy)" value={form.slug} onChange={change("slug")} required />
                <input className={field} placeholder="Name" value={form.name} onChange={change("name")} required />
                <input className={field} placeholder="Tagline (DE)" value={form.tagline_de} onChange={change("tagline_de")} required />
                <input className={field} placeholder="Tagline (EN)" value={form.tagline_en} onChange={change("tagline_en")} required />
                <textarea className={field} rows={2} placeholder="Body (DE)" value={form.body_de} onChange={change("body_de")} required />
                <textarea className={field} rows={2} placeholder="Body (EN)" value={form.body_en} onChange={change("body_en")} required />
                <input className={field} placeholder="Image URL" value={form.image} onChange={change("image")} required />
                <div className="flex gap-4 items-center">
                  <select className={field} value={form.status} onChange={change("status")}>
                    <option value="upcoming">Upcoming</option>
                    <option value="active">Active</option>
                  </select>
                  <label className="flex items-center gap-2 text-sm whitespace-nowrap">
                    <input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} /> Featured
                  </label>
                </div>
                <button type="submit" disabled={saving} className="flex items-center gap-2 border border-[#C5A880]/60 px-6 py-3 text-[12px] tracking-widest uppercase hover:bg-[#C5A880] hover:text-[#062029] transition-colors duration-500 disabled:opacity-50" data-testid="submit-gateway">
                  <Plus size={14} /> {saving ? "…" : "Add"}
                </button>
              </form>
            </div>
          </div>
        ) : (
          <div>
            <h2 className="font-serif-display text-3xl mb-8">Confidential Inquiries ({contacts.length})</h2>
            <div className="space-y-4">
              {contacts.length === 0 && <p className="text-[#F7F5F0]/50">No inquiries yet.</p>}
              {contacts.map((c) => (
                <div key={c.id} className="border border-[#F7F5F0]/12 p-6" data-testid="admin-contact-row">
                  <div className="flex justify-between flex-wrap gap-2">
                    <span className="font-serif-display text-xl">{c.name}</span>
                    <span className="text-[#C5A880] text-sm">{c.email}</span>
                  </div>
                  <p className="text-[#F7F5F0]/50 text-xs mt-1">{c.company} · {c.interest}</p>
                  <p className="text-[#F7F5F0]/80 mt-4 font-body">{c.message}</p>
                  <p className="text-[#F7F5F0]/30 text-xs mt-3">{new Date(c.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
